import { Controller } from '@hotwired/stimulus'
import { Chart, registerables } from 'chart.js'
import 'chartjs-adapter-date-fns'

// Register all Chart.js components
Chart.register(...registerables)

/**
 * Analytics Trend Chart Controller
 *
 * Displays a line chart showing metric trends over time
 */
export default class extends Controller {
  static values = {
    url: String,
    metric: String,
    days: { type: Number, default: 30 },
    totalTarget: String,
    chartId: String
  }

  connect() {
    this.loadChart()

    // Listen for period changes from select
    const periodSelect = document.getElementById('trendPeriod')
    if (periodSelect) {
      periodSelect.addEventListener('change', this.handlePeriodChange.bind(this))
    }
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  handlePeriodChange(event) {
    this.daysValue = parseInt(event.target.value, 10)
    this.loadChart()
  }

  async loadChart() {
    try {
      const url = `${this.urlValue}?metric=${this.metricValue}&days=${this.daysValue}`
      const response = await fetch(url)
      const data = await response.json()

      // Update total display if target specified
      if (this.hasTotalTargetValue) {
        const totalEl = document.getElementById(this.totalTargetValue)
        if (totalEl) {
          const changeText =
            data.change_percentage >= 0 ?
              `+${data.change_percentage}%` :
              `${data.change_percentage}%`
          const changeColor = data.change_percentage >= 0 ? 'var(--color-success-600)' : 'var(--color-error-600)'
          totalEl.innerHTML = `Total: <strong>${data.total}</strong> <span style="color: ${changeColor};">(${changeText})</span>`
        }
      }

      // Get canvas (use chartId if specified, otherwise find in element)
      let canvas
      if (this.hasChartIdValue) {
        canvas = document.getElementById(this.chartIdValue)
      } else {
        canvas = this.element.querySelector('canvas')
      }

      if (!canvas) {
        // eslint-disable-next-line no-console
        console.error('[AnalyticsTrend] Canvas not found')
        return
      }

      // Hide loading, show canvas
      const loadingDiv = this.element.querySelector('div')
      if (loadingDiv) loadingDiv.style.display = 'none'
      canvas.style.display = 'block'

      // Destroy existing chart if any
      if (this.chart) {
        this.chart.destroy()
      }

      const ctx = canvas.getContext('2d')

      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.data.map((d) => d.date),
          datasets: [
            {
              label: this.metricValue.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
              data: data.data.map((d) => d.count),
              borderColor: 'rgb(59, 130, 246)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              fill: true,
              tension: 0.3,
              pointRadius: 3,
              pointHoverRadius: 5
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: 'index',
              intersect: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0,0,0,0.05)' },
              ticks: {
                precision: 0
              }
            },
            x: {
              type: 'time',
              time: {
                unit: this.daysValue <= 7 ? 'day' : this.daysValue <= 30 ? 'day' : 'week',
                displayFormats: {
                  day: 'MMM d',
                  week: 'MMM d'
                }
              },
              grid: { display: false }
            }
          },
          interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
          }
        }
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[AnalyticsTrend] Error loading chart:', error)
      const loadingDiv = this.element.querySelector('div')
      if (loadingDiv) {
        loadingDiv.textContent = 'Failed to load chart'
        loadingDiv.style.color = 'var(--color-error-600)'
      }
    }
  }
}
